import { v4 as uuid } from 'uuid';

import { Type } from './NotificationContainer/components/Toasts/components/Toast/types';

export type NotificationMessage = string | React.ReactNode;

export interface Notification {
  id: string;
  type: Type;
  message: NotificationMessage;
  expiredOn: number;
  removed?: boolean;
  backgroundColor?: string;
  textColor?: string;
}

type ListenerFunction = (notification: Notification) => void;

const DEFAULT_TIMEOUT = 5000;

const listeners: ListenerFunction[] = [];

export const addListener = (fn: ListenerFunction) => {
  listeners.push(fn);

  return () => {
    const index = listeners.indexOf(fn);
    if (index > -1) {
      listeners.splice(index, 1);
    }
  };
};

const createNotification = ({
  type,
  message,
  timeOut,
  backgroundColor,
  textColor,
}: Pick<Notification, 'type' | 'message'> & {
  timeOut: number;
  backgroundColor?: string;
  textColor?: string;
}) => {
  const notification: Notification = {
    id: uuid(),
    type,
    message,
    expiredOn: timeOut === 0 ? 0 : Date.now() + timeOut,
    backgroundColor,
    textColor,
  };

  listeners.forEach(listener => listener(notification));

  return notification;
};

const success = (
  message: NotificationMessage,
  timeOut = DEFAULT_TIMEOUT,
  backgroundColor?: string,
  textColor?: string,
) =>
  createNotification({
    type: Type.SUCCESS,
    message,
    timeOut,
    backgroundColor,
    textColor,
  });

const error = (
  message: NotificationMessage,
  timeOut = DEFAULT_TIMEOUT,
  backgroundColor?: string,
  textColor?: string,
) =>
  createNotification({ type: Type.ERROR, message, timeOut, backgroundColor, textColor });

const warning = (
  message: NotificationMessage,
  timeOut = DEFAULT_TIMEOUT,
  backgroundColor?: string,
  textColor?: string,
) =>
  createNotification({
    type: Type.WARNING,
    message,
    timeOut,
    backgroundColor,
    textColor,
  });

const info = (
  message: NotificationMessage,
  timeOut = DEFAULT_TIMEOUT,
  backgroundColor?: string,
  textColor?: string,
) =>
  createNotification({ type: Type.INFO, message, timeOut, backgroundColor, textColor });

const loading = (
  message: NotificationMessage,
  timeOut = DEFAULT_TIMEOUT,
  backgroundColor?: string,
  textColor?: string,
) =>
  createNotification({
    type: Type.LOADING,
    message,
    timeOut,
    backgroundColor,
    textColor,
  });

const remove = (notification: Notification) => {
  // eslint-disable-next-line no-param-reassign
  notification.removed = true;
};

export default {
  success,
  error,
  warning,
  info,
  loading,
  remove,
};
